import React, { useState, useEffect } from 'react';
import { Typography, Box, CircularProgress, IconButton, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getProductCategories, getProducts, deleteProduct, getProductUnits } from '../../api/product';
import { Category, Product, ProductsByCategory, Unit } from '../../types/product';
import { useNotifications } from '../../hooks/useNotifications';
import { t } from '../../utils/translations';
import { styles } from './Products.styles';
import ProductEditPanel from './ProductEditPanel';

const Products: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productsByCategory, setProductsByCategory] = useState<ProductsByCategory>({});
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);

  const { showSuccess, showError } = useNotifications();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (categories.length > 0 && products.length > 0) {
      organizeProductsByCategory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, products]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesData, unitsData, productsData] = await Promise.all([
        getProductCategories(),
        getProductUnits(),
        getProducts(),
      ]);
      setCategories(categoriesData);
      setUnits(unitsData);
      setProducts(productsData);
    } catch (error) {
      showError(error instanceof Error ? error.message : t('products.loadFailed'), t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const organizeProductsByCategory = () => {
    const organized: ProductsByCategory = {};

    categories.forEach(category => {
      organized[category.key] = [];
    });

    products.forEach(product => {
      if (product.status === 1) {
        const categoryKey = product.category || 'none';
        if (organized[categoryKey]) {
          organized[categoryKey].push(product);
        }
      }
    });

    setProductsByCategory(organized);
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setIsEditPanelOpen(true);
  };

  const handleAdd = () => {
    setSelectedProduct(null);
    setIsEditPanelOpen(true);
  };

  const handleDelete = async (productId: string) => {
    if (!window.confirm(t('products.deleteConfirm'))) {
      return;
    }

    try {
      setDeletingProductId(productId);
      await deleteProduct(productId);
      setProducts(products.filter(p => p.id !== productId));
      showSuccess(t('products.deleteSuccess'), t('common.success'));
    } catch (error) {
      showError(error instanceof Error ? error.message : t('products.deleteFailed'), t('common.error'));
    } finally {
      setDeletingProductId(null);
    }
  };

  const handleCloseEditPanel = () => {
    setIsEditPanelOpen(false);
    setSelectedProduct(null);
  };

  const handleProductUpdated = () => {
    loadData();
    handleCloseEditPanel();
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {categories.length === 0 || products.length === 0 ? (
        <Box sx={styles.emptyState}>
          <Typography variant="body1">
            {categories.length === 0 ? t('products.noCategories') : t('products.noProducts')}
          </Typography>
        </Box>
      ) : (
        [...categories]
          .sort((a, b) =>
            t(`categories.${a.key}`).localeCompare(t(`categories.${b.key}`), 'pl')
          )
          .map(category => {
          const categoryProducts = productsByCategory[category.key] || [];

          if (categoryProducts.length === 0) {
            return null;
          }

          return (
            <Box key={category.key} sx={styles.categorySection}>
              <Typography variant="h6" sx={styles.categoryTitle}>
                {t(`categories.${category.key}`)}
              </Typography>
              {categoryProducts.map(product => (
                <Box key={product.id} sx={styles.productItem}>
                  <Box sx={styles.productInfo}>
                    <Typography sx={styles.productName}>{product.name}</Typography>
                    <Typography sx={styles.productUnit}>{t(`units.${product.unit}`)}</Typography>
                  </Box>
                  <Box sx={styles.productActions}>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(product)}
                      size="small"
                      aria-label={t('products.editProduct')}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(product.id)}
                      size="small"
                      disabled={deletingProductId === product.id}
                      aria-label={t('products.deleteProduct')}
                    >
                      {deletingProductId === product.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          );
        })
      )}

      <ProductEditPanel
        open={isEditPanelOpen}
        product={selectedProduct}
        categories={categories}
        units={units}
        onClose={handleCloseEditPanel}
        onUpdate={handleProductUpdated}
      />

      <Box sx={styles.addButtonContainer}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          fullWidth
          sx={styles.addButton}
        >
          {t('products.addProduct')}
        </Button>
      </Box>
    </Box>
  );
};

export default Products;